import { ChangeEvent, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import AppButton from '../../../../../../components/common/AppButton';
import { SVG } from '../../../../../../constants/assetLists';
import {
  MediaFieldValue,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
} from '../../types';
import { getMediaFieldPreviewUrl } from '../../../../buildOnBoardingDraftPayload';
import AppTextInput from '../../../../../../components/common/AppTextInput';
import AppMUIAutocomplete from '../../../../../../components/common/AppMUIAutoComplete';
import { useOnBoardingContext } from '../../../../../../context/onBoarding/OnBoardingContextProvider';
import { getActiveSectionText } from '../../../../../../utils';
import { nodeRequiredDetailsSchema, nodeSetupCompleteSchema } from '../../networkDetailsSchema';
import OnboardingValidationBadge from '../OnboardingValidationBadge';

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
  expanded: boolean;
  onToggle: (id: string) => void;
};

const BRANCH_TYPE_OPTIONS = [
  { label: 'מסעדה', value: 'restaurant' },
  { label: 'בית קפה', value: 'cafe' },
  { label: 'בר', value: 'bar' },
  { label: 'חנות', value: 'store' },
  { label: 'מאפיה', value: 'bakery' },
  { label: 'פיצריה', value: 'pizzeria' },
  { label: 'קבוצה', value: 'group' },
] as const;

function BranchDetails({ selectedFieldPath, expanded, onToggle }: Props) {
  const { setShowSetupNetworkScreen } = useOnBoardingContext();

  const { control, watch } =
    useFormContext<NetworkDetailsFormValues>();
  const logoInputId = `${selectedFieldPath}.id`;
  const mobileLogoInputId = `${selectedFieldPath}.mobileLogo.id`;

  const fieldValue = selectedFieldPath && watch(selectedFieldPath);
  const isFormValid =
      nodeRequiredDetailsSchema.safeParse(fieldValue).success;
  const isSetupValid = nodeSetupCompleteSchema.safeParse(fieldValue).success;
  const isNodeValid = isFormValid && isSetupValid;

  const openNetworkSetupScreen = async () => {
    setShowSetupNetworkScreen(true);
  };

  const activeSectionText = getActiveSectionText(
    selectedFieldPath?.toString() ?? '',
  );

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="w-[12px] h-[12px]"
              onClick={() => onToggle('branch')}
            >
              <img
                src={expanded ? SVG.expandIcon : SVG.lessThanIcon}
                alt="caret"
                className="w-full h-full object-contain"
              />
            </button>

            <div className="flex items-center gap-1">
              <span className="text-base font-semibold text-raspberryRed">*</span>
              <p className="text-base font-semibold text-textColor">
                פרטי {activeSectionText}
              </p>
            </div>
          </div>

          <OnboardingValidationBadge
            show={!isNodeValid}
            text={
              !isFormValid
                ? 'השלם את הנתונים'
                : `השלם את הגדרות ${activeSectionText}`
            }
          />
        </div>

        <AppButton
          variant="custom"
          className="bg-linkBlue text-white hover:opacity-90"
          iconStart={
            <img
              src={SVG.editSettingsWhiteIcon}
              alt="edit"
              className="w-[15px] h-[15px] object-contain"
            />
          }
          onClick={openNetworkSetupScreen}
        >
          הגדרת {activeSectionText}
        </AppButton>
      </div>

      {expanded && (
        <div className="flex items-center gap-3">
          <div className="flex-1 flex flex-col gap-3">
            <Controller
              control={control}
              name={`${selectedFieldPath}.name`}
              render={({ field, fieldState: { error } }) => (
                <AppTextInput
                  label={`שם ${activeSectionText}`}
                  placeholder={`שם ${activeSectionText}`}
                  required
                  className="border-none"
                  {...field}
                  error={error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name={`${selectedFieldPath}.type`}
              render={({ field, fieldState: { error } }) => (
                <AppMUIAutocomplete
                  label="סוג (מסעדה/בית קפה/בר/מאפיה/פיצריה/קבוצה...)"
                  options={[...BRANCH_TYPE_OPTIONS]}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  size="small"
                  value={
                    BRANCH_TYPE_OPTIONS.find(
                      (option) => option.value === field.value,
                    ) ?? null
                  }
                  onChange={(_event, option) =>
                    field.onChange(option?.value ?? '')
                  }
                  onBlur={field.onBlur}
                  required
                  error={!!error?.message}
                  helperText={error?.message}
                />
              )}
            />
          </div>

          <div className="flex items-center gap-2">
            <div className="flex flex-col gap-1">
              <Controller
                control={control}
                name={`${selectedFieldPath}.mobileLogo`}
                render={({ field }) => (
                  <BranchLogoField
                    logo={field.value}
                    inputId={mobileLogoInputId}
                    onLogoChange={field.onChange}
                    logoLabel="העלאת לוגו לאפליקציה על הסלולרי"
                    replaceLogoLabel="החלפת לוגו (סלולרי)"
                  />
                )}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Controller
                control={control}
                name={`${selectedFieldPath}.logo`}
                render={({ field }) => (
                  <BranchLogoField
                    logo={field.value}
                    inputId={logoInputId}
                    onLogoChange={field.onChange}
                    logoLabel="העלאת לוגו לאפליקציה על המחשב"
                    replaceLogoLabel="החלפת לוגו (מחשב)"
                  />
                )}
              />
            </div>

            <span className="w-[147px] text-base text-textColor">
              יש להעלות קבצים בפורמט PNG, JPG בלבד.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

type BranchLogoFieldProps = {
  logo: MediaFieldValue;
  inputId: string;
  onLogoChange: (logo: MediaFieldValue) => void;
  logoLabel?: string;
  replaceLogoLabel?: string;
};

function BranchLogoField({
  logo,
  inputId,
  onLogoChange,
  logoLabel,
  replaceLogoLabel,
}: BranchLogoFieldProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!logo) {
      setPreviewUrl(null);
      return;
    }

    const mediaUrl = getMediaFieldPreviewUrl(logo);
    if (mediaUrl) {
      setPreviewUrl(mediaUrl);
      return;
    }

    if (logo instanceof File) {
      const objectUrl = URL.createObjectURL(logo);
      setPreviewUrl(objectUrl);

      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [logo]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    onLogoChange(file);
    e.target.value = '';
  };

  const hasLogo = Boolean(logo && previewUrl);

  return (
    <div className="relative w-[202px] h-[116px] rounded-lg border border-gray90 bg-gray90 flex items-center justify-center overflow-hidden">
      <input
        id={inputId}
        type="file"
        hidden
        accept="image/png, image/jpeg, image/jpg"
        onChange={handleFileChange}
      />

      {hasLogo ? (
        <>
          <img
            src={previewUrl!}
            alt="לוגו הסניף"
            className="w-full h-full object-contain rounded-lg"
          />

          <label
            htmlFor={inputId}
            className="absolute bottom-0.5 left-0 right-0 bg-[#007AFF] text-white text-sm text-center max-w-fit mx-auto px-2 py-0.5 rounded-[10px] cursor-pointer"
          >
            {replaceLogoLabel || 'החלפת לוגו'}
          </label>
        </>
      ) : (
        <label
          htmlFor={inputId}
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
        >
          <div className="w-[80px] h-[80px] pointer-events-none">
            <img
              src={SVG.imgPlaceholderWhiteIcon}
              alt="icon"
              className="w-full h-full object-contain"
            />
          </div>

          <span className="absolute left-50% top-50% transform -translate-x-50% -translate-y-50% text-base text-center text-textColor pointer-events-none">
            {logoLabel || 'העלאת לוגו'}
          </span>
        </label>
      )}
    </div>
  );
}

export default BranchDetails;
